import * as THREE from 'three';
import {
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';
import { aperture } from 'ramda';
import { ThreeEvent } from '@react-three/fiber';
import { UnitSystem } from 'providers/api';
import { distanceMetricToImperial } from 'utils/unitConversions';
import { Model } from '../../hooks/useModelState';
import { returnShortestRoute } from '../Route/utils';
import * as PCConstants from '../PCConstants';
import * as GConstants from '../GConstants';
import PCRoute from '../Route/PCRoute';
import { cadToThreeJsPositionWithOffset } from '../../Scenes/scene-utils';

interface RoutesMeshProps {
  model: Model;
  offset: THREE.Vector3;
  scale: number;
  onRouteFocus?: (route: PCRoute, worldPosition: THREE.Vector3) => void;
}
const CHART_HOVER_EVENT = 'hgl-chart-hover';
const ROUTE_HOVER_EVENT = 'hgl-route-hover';

interface RoutePosition {
  distance: number;
  position: THREE.Vector3;
}

// Distance from pump at the route point nearest `target`, interpolated along the closest leg of the route.
const distanceAlongRoute = (points: RoutePosition[], target: THREE.Vector3): number | null => {
  const legs: [RoutePosition, RoutePosition][] = aperture(2, points);
  const nearest = legs.reduce<{ distance: number; gapSq: number } | null>((best, [start, end]) => {
    const leg = new THREE.Line3(start.position, end.position);
    const t = leg.closestPointToPointParameter(target, true);
    const gapSq = leg.at(t, new THREE.Vector3()).distanceToSquared(target);
    return best && best.gapSq <= gapSq ? best : { distance: start.distance + (end.distance - start.distance) * t, gapSq };
  }, null);
  return nearest?.distance ?? null;
};

const RoutesMesh = ({ model, offset, scale, onRouteFocus }: RoutesMeshProps) => {
  const shortestRoute = useMemo(() => {
    if (!model.routes || Object.keys(model.routes).length === 0) return undefined;
    return returnShortestRoute(model.routes);
  }, [model.routes]);

  const selectedRoute = useMemo(() => model.getSelectedRoute(), [model.routes]);
  const primaryRoute = selectedRoute || shortestRoute;
  const [hoverMarker, setHoverMarker] = useState<THREE.Vector3 | null>(null);

  const toDisplayDistance = useCallback((distance: number | undefined) => {
    if (typeof distance !== 'number') return 0;
    return model.project?.unitSystemPreference === UnitSystem.Imperial
      ? distanceMetricToImperial(distance)
      : distance;
  }, [model.project?.unitSystemPreference]);

  const primaryRoutePoints = useMemo((): RoutePosition[] => {
    const points = primaryRoute?.routePoints;
    if (!points || points.length === 0) return [];
    const dischargeNodeCount = primaryRoute?.stope?.data?.pipeArrays?.length || 0;
    const filteredPoints = points.slice(dischargeNodeCount);
    return filteredPoints.map((p) => ({
      distance: toDisplayDistance(p.distanceFromPump),
      position: new THREE.Vector3(...cadToThreeJsPositionWithOffset(p.point.coords, offset, scale)),
    }));
  }, [primaryRoute, toDisplayDistance, offset, scale]);

  const routeMeshes = useMemo(() => {
    const entries = Object.entries(model.routes || {});
    const shortestRouteId = entries.find(([, route]) => route === shortestRoute)?.[0];
    const selectedRouteId = entries.find(([, route]) => route === selectedRoute)?.[0];

    const routeToShowIds = selectedRouteId || shortestRouteId;

    return entries.flatMap(([routeId, route]) => {
      const { routePoints } = route;
      if (!routePoints || routePoints.length < 2) return [];

      const dischargeNodeCount = route?.stope?.data?.pipeArrays?.length || 0;
      const filteredRoutePoints = routePoints.slice(dischargeNodeCount);

      if (filteredRoutePoints.length < 2) return [];

      const pts = filteredRoutePoints.map((p) => ({
        vec: new THREE.Vector3(...cadToThreeJsPositionWithOffset(p.point.coords, offset, scale)),
        distance: toDisplayDistance(p.distanceFromPump),
      }));

      const isPrimaryRoute = routeId === routeToShowIds;
      const radius = (isPrimaryRoute ? 1.2 : 0.6) * scale;
      const defaultColour = isPrimaryRoute ? PCConstants.SPOOL_SELECTED_COLOUR : PCConstants.SPOOL_ACTIVE_COLOUR;
      const hasSegmentColours = route.routeSegmentColours.length > 0;
      const segmentColours = hasSegmentColours
        ? route.routeSegmentColours
        : [defaultColour];

      const allSameColour = !hasSegmentColours || segmentColours.every((c) => c === segmentColours[0]);

      if (allSameColour) {
        const curve = new THREE.CatmullRomCurve3(pts.map((p) => p.vec), false, 'catmullrom', 0);
        return [{
          routeId,
          segmentIndex: 0,
          curve: curve as THREE.Curve<THREE.Vector3>,
          radius,
          colour: segmentColours[0] || defaultColour,
          interactive: isPrimaryRoute,
          tubularSegments: Math.max(12, (pts.length - 1) * 6),
        }];
      }

      const segments: Array<{
        routeId: string;
        segmentIndex: number;
        curve: THREE.Curve<THREE.Vector3>;
        radius: number;
        colour: string;
        interactive: boolean;
        tubularSegments: number;
      }> = [];

      for (let i = 0; i < pts.length - 1; i += 1) {
        const curve = new THREE.LineCurve3(pts[i].vec, pts[i + 1].vec);
        const segmentColour = segmentColours[i] || segmentColours[segmentColours.length - 1] || defaultColour;
        segments.push({
          routeId,
          segmentIndex: i,
          curve,
          radius,
          colour: segmentColour,
          interactive: isPrimaryRoute,
          tubularSegments: 2,
        });
      }

      return segments;
    });
  }, [model.routes, shortestRoute, offset, scale, selectedRoute, toDisplayDistance]);

  const dispatchRouteHover = useCallback((distance: number | null) => {
    window.dispatchEvent(new CustomEvent(ROUTE_HOVER_EVENT, { detail: { distance } }));
  }, []);

  // The tube's mesh has no transform of its own, so its local space is the space the route points were built in.
  const handleRoutePointerMove = (event: ThreeEvent<PointerEvent>) => {
    if (event.nativeEvent.buttons) return;
    dispatchRouteHover(distanceAlongRoute(primaryRoutePoints, event.object.worldToLocal(event.point.clone())));
  };

  const findPositionOnPrimaryRoute = useCallback((distance: number) => {
    if (primaryRoutePoints.length < 2) return null;
    for (let i = 0; i < primaryRoutePoints.length - 1; i += 1) {
      const start = primaryRoutePoints[i + 1];
      const end = primaryRoutePoints[i];
      const minDistance = Math.min(start.distance, end.distance);
      const maxDistance = Math.max(start.distance, end.distance);
      if (distance >= minDistance && distance <= maxDistance) {
        const span = maxDistance - minDistance || 1;
        const t = (distance - start.distance) / span;
        return new THREE.Vector3(
          start.position.x + (end.position.x - start.position.x) * t,
          (start.position.y + (end.position.y - start.position.y) * t) + 12 * scale,
          start.position.z + (end.position.z - start.position.z) * t,
        );
      }
    }
    return null;
  }, [primaryRoutePoints]);

  useEffect(() => {
    const handleChartHover = (evt: Event) => {
      const distance = (evt as CustomEvent<{ distance: number | null }>).detail?.distance;
      if (typeof distance !== 'number') {
        setHoverMarker(null);
        return;
      }
      setHoverMarker(findPositionOnPrimaryRoute(distance));
    };

    window.addEventListener(CHART_HOVER_EVENT, handleChartHover);
    return () => window.removeEventListener(CHART_HOVER_EVENT, handleChartHover);
  }, [findPositionOnPrimaryRoute]);

  useEffect(() => {
    setHoverMarker(null);
  }, [primaryRoute]);

  const handleRouteDoubleClick = (routeId: string) => {
    if (onRouteFocus && model.routes) {
      const route = model.routes[routeId];
      if (route && route.routePoints && route.routePoints.length > 0) {
        const firstPoint = route.routePoints[0];
        if (firstPoint && firstPoint.point) {
          const [x, y, z] = cadToThreeJsPositionWithOffset(firstPoint.point.coords, offset, scale);
          const worldPosition = new THREE.Vector3(x, y, z);
          onRouteFocus(route, worldPosition);
        }
      }
    }
  };

  return (
    <group>
      {hoverMarker && (
        <>
          <arrowHelper
            args={[
              new THREE.Vector3(0, -1, 0),
              hoverMarker,
              12 * scale,
              GConstants.YELLOW,
              6 * scale,
              6 * scale,
            ]}
          />
          <mesh position={[hoverMarker.x, hoverMarker.y - (12 * scale), hoverMarker.z]}>
            <sphereGeometry args={[2.5 * scale, 12, 12]} />
            <meshBasicMaterial color={GConstants.YELLOW} />
          </mesh>
        </>
      )}
      {routeMeshes.map(
        (segment) => (
          <mesh
            key={`${segment.routeId}-${segment.segmentIndex}`}
            onDoubleClick={() => handleRouteDoubleClick(segment.routeId)}
            onPointerMove={segment.interactive ? handleRoutePointerMove : undefined}
            onPointerOut={segment.interactive ? (e: any) => { if (e.nativeEvent?.buttons) return; dispatchRouteHover(null); } : undefined}
            onPointerCancel={segment.interactive ? () => dispatchRouteHover(null) : undefined}
            userData={{ routeId: segment.routeId }}
          >
            <tubeGeometry args={[segment.curve, segment.tubularSegments, segment.radius, 6, false]} />
            <meshBasicMaterial color={segment.colour} transparent opacity={1} />
          </mesh>
        ),
      )}
    </group>
  );
};

export default RoutesMesh;
